/**
 * Live summariser broadcasts — policy changes, drain progress, written
 * summaries and settled fires — delivered through the shared `global_events`
 * room.
 *
 * This domain exists because that room has exactly one entry point: a
 * client-emitted `subscribe_global`. `websocketManager.subscribe()` only
 * attaches a local handler and never sends it, so a console built on that
 * alone would compile, run, and silently receive nothing (PRD-0210 D-14).
 */

import {
  type SummarizationEventMessage,
  type SummarizationEventName,
} from '@/types/summarization'
import { WebSocketClient } from '../client'
import { SubscriptionRegistry } from '../subscriptions'

export class SummarizationLiveApi {
  private readonly events: SubscriptionRegistry<
    SummarizationEventName,
    SummarizationEventMessage
  >

  constructor(private readonly client: WebSocketClient) {
    this.events = new SubscriptionRegistry({
      // Joins the shared `global_events` room; subscribe_global is idempotent
      // server-side. There is deliberately no leave — the activity feed and
      // the profile-change subscription share the room, and leaving would
      // break those subscribers.
      join: () => this.client.emitWhenConnected('subscribe_global', {}),
    })
  }

  /**
   * Subscribe to one summarisation broadcast. The callback receives the
   * `{ type, data }` envelope, so the payload is read off `.data` — the shape
   * every shipped payload reader in this codebase unwraps.
   */
  subscribeToSummarizationEvent(
    event: SummarizationEventName,
    callback: (message: SummarizationEventMessage) => void
  ): () => void {
    if (!this.client.hasSocket()) {
      console.warn('WebSocket not connected. Call connect() first.')
      return () => {}
    }

    this.client.attachOnce(event, (data) => {
      this.events.dispatch(event, { type: event, data })
    })

    return this.events.subscribe(event, callback)
  }

  /** Re-join global_events for every live summarisation listener. */
  replay(): void {
    this.events.replay()
  }

  clear(): void {
    this.events.clear()
  }
}
